import React, { useState, useEffect, useContext } from "react";

import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { isEmpty } from "lodash";
import { useHistory } from "react-router";
import { Trans, useTranslation } from "react-i18next";
import { BlockPicker } from "react-color";

import OrganizationProfileSchema from "../../schemas/organizationProfile.schema";
import OrganizationsAPI from "../../API/organizations.api";
import { SuccessAlert, ErrorAlert } from "../../utils/Alerts";
import LookupsAPI from "../../API/lookups.api";
import useClickOutside from "../../utils/CustomHooks/useClickOutside";
import { COLOR_PICKER_DEFAULTS } from "../../utils/constants";
import EditableImage from "../../components/Images/EditableImages";
import LogCapture from "../../utils/LogManagement/LogHelper";
import ValidationError from "../Shared/ValidationError";
import { validate } from "../../utils/ValidationHelpers";
import { getCountriesDropdown } from "../../utils/CountryHelper";
import { getReactSelectTheme } from "../../utils/ReactSelectStyles";
import { fetchLoggedUser } from "../../utils/UserHelper";
import { getEncodedValue, removeUnchangedProps } from "../../utils/Helpers";
import { getReactSelectStyling } from "../../utils/ReactSelectStyles";
import { getCurrencyDropdown } from "../../utils/CurrencyHelper";
import UserContext from "../../lib/UserContext";

const OrganizationProfileForm = ({
    organization,
    countries,
    setCountries,
    setEditMode,
    flag,
    externalOrganization,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [error, setError] = useState({});
    const [industries, setIndustries] = useState([]);
    const [isIndustriesLoading, setIsIndustriesLoading] = useState(false);
    const [currencies, setCurrencies] = useState([]);
    const [currentInput, setCurrentInput] = useState({
        ...organization,
        country: undefined,
        abbreviation: organization?.abbreviation,
        supportedCountryId: organization?.country?.value || null,
        industries: [],
        backgroundColor:
            organization?.backgroundColor || COLOR_PICKER_DEFAULTS[0],
        defaultCurrency: null,
    });
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [colorPickerColor, setColorPickerColor] = useState(() => {
        return organization.backgroundColor || COLOR_PICKER_DEFAULTS[0];
    });

    const history = useHistory();
    const { t } = useTranslation();

    const currentUser = useContext(UserContext).getUser();
    const isDefaultCurrencyEditable =
        currentUser.organization.isDefaultCurrencyEditable;

    useEffect(() => {
        if (isEmpty(countries)) {
            getCountriesDropdown(setCountries, true);
        }
        getLookups();
        getCurrencyDropdown(setCurrencies);
    }, []);

    useEffect(() => {
        const defaultCurrencyId = currentUser.organization.defaultCurrencyId;
        if (defaultCurrencyId)
            setCurrentInput({
                ...currentInput,
                defaultCurrency: currencies.find(
                    (elem) => elem.value === defaultCurrencyId
                ),
            });
        else if (currencies)
            setCurrentInput({
                ...currentInput,
                defaultCurrency: currencies[0],
            });
    }, [currencies]);

    useEffect(() => {
        validate(
            currentInput,
            OrganizationProfileSchema.OrganizationProfileSchema,
            setIsFormValid,
            setError
        );
    }, [currentInput]);

    const toggleColorPicker = () => {
        setShowColorPicker((prev) => !prev);
    };

    const closeColorPicker = () => {
        setShowColorPicker(false);
    };

    const colorPickerRef = useClickOutside(closeColorPicker);

    const getLookups = async () => {
        try {
            setIsIndustriesLoading(true);
            const result = await LookupsAPI.getIndustries();
            const mappedResults = result.map((record) => {
                return { ...record };
            });

            setIndustries(mappedResults);
        } catch (responseError) {
            console.log(responseError);
            ErrorAlert(t("couldntGetIndustriesMessage"));
        } finally {
            setIsIndustriesLoading(false);
        }
    };

    const handleInputChange = (event) => {
        setCurrentInput({
            ...currentInput,
            [event.target.name]: event.target.value,
        });
    };
    const handleIndustryChange = (newValue, action) => {
        let newCurrentInput = { ...currentInput };
        let newRemoved;
        switch (action.action) {
            case "create-option":
                if (action.option.label.trim()) {
                    newCurrentInput.industries.push({
                        name: action.option.label.trim(),
                        isCreated: true,
                    });
                }
                break;
            case "select-option":
                newCurrentInput.industries.push({
                    id: action.option.value,
                });
                break;
            case "remove-value":
                if (action.removedValue.label === action.removedValue.value) {
                    newCurrentInput.industries =
                        newCurrentInput.industries.filter((industry) => {
                            return industry.name !== action.removedValue.label;
                        });
                } else {
                    let addIsDeleted = true;
                    newCurrentInput.industries =
                        newCurrentInput.industries.filter((industry) => {
                            if (industry.id === action.removedValue.value) {
                                addIsDeleted = false;
                            }
                            return industry.id !== action.removedValue.value;
                        });
                    if (addIsDeleted) {
                        newCurrentInput.industries.push({
                            id: action.removedValue.value,
                            isDeleted: true,
                        });
                    }
                }
                break;
            case "clear":
                newRemoved = [];
                action.removedValues.forEach((record) => {
                    if (record.value !== record.label) {
                        newRemoved.push({ id: record.value, isDeleted: true });
                    }
                });
                newCurrentInput.industries = [...newRemoved];
        }
        setCurrentInput({
            ...newCurrentInput,
            industries: [...newCurrentInput.industries],
        });
    };

    const handleCountryChange = (newValue) => {
        setCurrentInput({
            ...currentInput,
            supportedCountryId: newValue?.value,
        });
    };

    const handleCurrencyChange = (newValue) => {
        setCurrentInput({
            ...currentInput,
            defaultCurrency: {
                value: newValue?.value,
                label: currencies.find((c) => c.value === newValue?.value)
                    .label,
            },
        });
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setIsFormSubmitted(true);
        if (!isFormValid) {
            setIsSubmitting(false);
            return;
        }

        try {
            delete currentInput.hasTermsAndConditions;
            delete currentInput.hasPrivacyPolicy;

            const filteredInput = removeUnchangedProps(
                organization,
                currentInput
            );

            let defaultCurrencyId = currentInput.defaultCurrency.value;
            delete filteredInput.defaultCurrency;

            const result = await OrganizationsAPI.updateOrganizationInfo({
                ...filteredInput,
                id: organization.id,
                name: filteredInput.name?.trim(),
                about: filteredInput.about?.trim(),
                address: filteredInput.address?.trim(),
                email: filteredInput.email?.trim(),
                image: currentInput.image,
                termsAndConditionsFileURI:
                    filteredInput.termsAndConditionsFileURI?.data,
                privacyPolicyFileURI: filteredInput.privacyPolicyFileURI?.data,
                defaultCurrencyId: defaultCurrencyId,
            });
            if (result?.modified) {
                LogCapture.info(
                    `Affiliate profile updated with name: ${currentInput?.name} & id: ${currentInput?.id}`
                );
                setEditMode(false);
                SuccessAlert(t("editAffiliateSuccessMessage"));
                fetchLoggedUser();

                const formattedName = getEncodedValue(
                    externalOrganization.name
                );
                history.push(
                    isEmpty(externalOrganization)
                        ? "/affiliate-profile"
                        : `/partners/profile/${formattedName}`
                );
            } else {
                LogCapture.error(
                    `Couldn't update affiliate profile with name: ${currentInput?.name} & id: ${currentInput?.id}, error: ${error}`
                );
            }
        } catch (e) {
            ErrorAlert(t("editAffiliateFailMessage"));
            LogCapture.error(
                `Couldn't update affiliate profile with name: ${currentInput?.name} & id: ${currentInput?.id}, error: ${error}`
            );
            console.log(e);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUploadFile = (e) => {
        const acceptedFileExtensions = e.target.accept.split(",");
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (acceptedFileExtensions.includes(file.type)) {
                let reader = new FileReader();
                const fieldName = e.target.name;

                reader.onload = function (event) {
                    setCurrentInput({
                        ...currentInput,
                        [fieldName]: {
                            name: file.name,
                            data: event.target.result,
                        },
                    });
                };

                reader.readAsDataURL(file);
            } else {
                setError((prevError) => {
                    return {
                        ...prevError,
                        [e.target
                            .name]: `must be of type ${acceptedFileExtensions}`,
                    };
                });
            }
        } else {
            setCurrentInput({
                ...currentInput,
                [e.target.name]: e.target.value,
            });
        }
        setError((prevError) => {
            return {
                ...prevError,
                [e.target.name]: null,
            };
        });
    };

    const handleColorPickerChange = (color) => {
        setColorPickerColor(color.hex);
        setCurrentInput((prevInput) => {
            return { ...prevInput, backgroundColor: color.hex };
        });
        setShowColorPicker(false);
    };

    const inputValueHandler = (property) => {
        return property ? property : "";
    };

    const renderImageSelection = () => {
        return (
            <EditableImage
                state={currentInput}
                stateSetter={setCurrentInput}
                defaultImage={
                    require("../../assets/images/org-avatar.png").default
                }
            />
        );
    };

    const onCancelClick = () => {
        if (isEmpty(externalOrganization)) setEditMode(false);
        else {
            const formattedName = getEncodedValue(externalOrganization.name);
            history.push(`/partners/profile/${formattedName}`);
        }
    };

    return (
        <>
            <div className="card-table shadow">
                <div className="upload-img-box flex">
                    <div className="relative">{renderImageSelection()}</div>
                    <div className="w-full">
                        <h3 className="medium-title-bold">
                            <Trans i18nKey="affiliateInformation">
                                Affiliate Information
                            </Trans>
                        </h3>
                        <hr className="mt-2 mb-5" />
                        <div
                            className={`tags input-group ${
                                error.name && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <h4 className="uppercase text-xs required">
                                <Trans i18nKey="name">Name</Trans>
                            </h4>
                            <input
                                type="text"
                                name="name"
                                value={currentInput.name}
                                onChange={handleInputChange}
                                className="input-element"
                                placeholder="Affiliate name"
                            />
                            {isFormSubmitted && (
                                <ValidationError errorMessage={error.name} />
                            )}
                        </div>
                        <div
                            className={`tags input-group ${
                                error.email && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <h4 className="uppercase text-xs required">
                                <Trans i18nKey="email">Email</Trans>
                            </h4>
                            <input
                                type="email"
                                name="email"
                                value={inputValueHandler(currentInput.email)}
                                onChange={handleInputChange}
                                className="input-element"
                                placeholder="Affiliate email"
                            />
                            {isFormSubmitted && (
                                <ValidationError errorMessage={error.email} />
                            )}
                        </div>
                        <div className="flex flex-column-start-mobile">
                            <div className="tags input-group w-full">
                                <h4 className="uppercase text-xs required">
                                    <Trans i18nKey="defaultCurrency">
                                        Default Currency
                                    </Trans>
                                </h4>

                                <Select
                                    className="basic-single"
                                    classNamePrefix="select"
                                    name="defaultCurrency"
                                    placeholder={t("selectPlaceholder", {
                                        entity: t("currency"),
                                    })}
                                    options={currencies}
                                    value={currentInput.defaultCurrency}
                                    onChange={handleCurrencyChange}
                                    theme={getReactSelectTheme}
                                    isDisabled={!isDefaultCurrencyEditable}
                                />

                                {isFormSubmitted && (
                                    <ValidationError
                                        errorMessage={
                                            error.defaultCurrency?.value
                                        }
                                    />
                                )}
                            </div>
                            <span className="mx-2"></span>
                            <div
                                className={`tags input-group w-full ${
                                    error.abbreviation && isFormSubmitted
                                        ? "invalid-warning-input"
                                        : ""
                                }`}
                            >
                                <h4 className="uppercase text-xs">
                                    <Trans i18nKey="abbreviation">
                                        Abbreviation
                                    </Trans>
                                </h4>
                                <input
                                    type="text"
                                    name="abbreviation"
                                    value={inputValueHandler(
                                        currentInput.abbreviation
                                    )}
                                    style={{ textTransform: "uppercase" }}
                                    onChange={(e) => {
                                        e.target.value =
                                            e.target.value.toUpperCase();
                                        handleInputChange(e);
                                    }}
                                    className="input-element"
                                    placeholder="e.g. ABC"
                                />
                                {isFormSubmitted && (
                                    <ValidationError
                                        errorMessage={error.abbreviation}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="flex flex-column-start-mobile">
                            <div className="tags input-group w-full">
                                <h4 className="uppercase text-xs required">
                                    <Trans i18nKey="country">Country</Trans>
                                </h4>
                                {flag !== null && (
                                    <Select
                                        className="basic-single"
                                        classNamePrefix="select"
                                        isClearable
                                        isSearchable
                                        name="supportedCountryId"
                                        placeholder="Select Country..."
                                        options={countries}
                                        getOptionValue={(option) =>
                                            option.searchable
                                        }
                                        onChange={handleCountryChange}
                                        defaultValue={
                                            !isEmpty(organization.country)
                                                ? {
                                                      value: organization
                                                          .country?.value,
                                                      label: flag,
                                                  }
                                                : ""
                                        }
                                        theme={getReactSelectTheme}
                                    />
                                )}
                                {isFormSubmitted && (
                                    <ValidationError
                                        errorMessage={error.supportedCountryId}
                                    />
                                )}
                            </div>
                            <span className="mx-2"></span>
                            <div
                                className={`tags w-full input-group ${
                                    error.address && isFormSubmitted
                                        ? "invalid-warning-input"
                                        : ""
                                }`}
                            >
                                <h4 className="uppercase text-xs">
                                    <Trans i18nKey="address">Address</Trans>
                                </h4>
                                <input
                                    type="text"
                                    name="address"
                                    value={inputValueHandler(
                                        currentInput.address
                                    )}
                                    onChange={handleInputChange}
                                    className="input-element"
                                    placeholder="Enter Affiliate Address"
                                />
                            </div>
                        </div>
                        <div
                            className={`tags input-group ${
                                error.about && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <h4 className="uppercase text-xs">
                                <Trans i18nKey="about">About</Trans>
                            </h4>
                            <textarea
                                value={inputValueHandler(currentInput.about)}
                                id="about"
                                name="about"
                                className="input-element textarea-element"
                                onChange={handleInputChange}
                                placeholder="Write a brief description about your Affiliate"
                            ></textarea>
                            {isFormSubmitted && (
                                <ValidationError errorMessage={error.about} />
                            )}
                        </div>
                        <div className="tags">
                            <h4 className="uppercase text-xs">
                                <Trans i18nKey="links">links</Trans>
                            </h4>
                            <div className="flex-col">
                                <div
                                    className={`mb-1 relative input-group ${
                                        error.website && isFormSubmitted
                                            ? "invalid-warning-input"
                                            : ""
                                    }`}
                                >
                                    <i className="fas fa-link fa-xs icon-input"></i>
                                    <span className="input-separat-line"></span>
                                    <input
                                        type="text"
                                        name="website"
                                        value={inputValueHandler(
                                            currentInput.website
                                        )}
                                        onChange={handleInputChange}
                                        className="input-element input-icon-padding"
                                        placeholder="Official Webstie"
                                    />
                                    {isFormSubmitted && (
                                        <ValidationError
                                            errorMessage={error.website}
                                        />
                                    )}
                                </div>
                                <div
                                    className={`mb-4 input-group relative ${
                                        error.linkedin && isFormSubmitted
                                            ? "invalid-warning-input"
                                            : ""
                                    }`}
                                >
                                    <i className="fab fa-linkedin fa-sm icon-input"></i>
                                    <span className="input-separat-line"></span>
                                    <input
                                        type="text"
                                        name="linkedin"
                                        value={inputValueHandler(
                                            currentInput.linkedin
                                        )}
                                        onChange={handleInputChange}
                                        className="input-element input-icon-padding"
                                        placeholder="LinkedIn Profile"
                                    />
                                    {isFormSubmitted && (
                                        <ValidationError
                                            errorMessage={error.linkedin}
                                        />
                                    )}
                                </div>
                                <div
                                    className={`mb-4 input-group ${
                                        error.privacyPolicyFileURI &&
                                        isFormSubmitted
                                            ? "invalid-warning-input"
                                            : ""
                                    }`}
                                >
                                    <h4 className="uppercase text-xs">
                                        <Trans i18nKey="privacyPolicy">
                                            Privacy Policy
                                        </Trans>
                                    </h4>
                                    <div className="file-upload-wrapper">
                                        <input
                                            type="file"
                                            className="file-upload-input"
                                            aria-label="File browser"
                                            name="privacyPolicyFileURI"
                                            onChange={handleUploadFile}
                                            accept="application/pdf"
                                        />
                                        <label className="file-upload-label">
                                            {currentInput.privacyPolicyFileURI
                                                ?.name || (
                                                <Trans i18nKey="filePlaceholder">
                                                    Choose File...
                                                </Trans>
                                            )}
                                        </label>
                                        {isFormSubmitted && (
                                            <ValidationError
                                                errorMessage={
                                                    error.privacyPolicyFileURI
                                                }
                                            />
                                        )}
                                    </div>
                                </div>
                                <div
                                    className={`mb-4 input-group ${
                                        error.termsAndConditionsFileURI &&
                                        isFormSubmitted
                                            ? "invalid-warning-input"
                                            : ""
                                    }`}
                                >
                                    <h4 className="uppercase text-xs">
                                        <Trans i18nKey="termsAndConditions">
                                            Terms And Conditions
                                        </Trans>
                                    </h4>
                                    <div className="file-upload-wrapper">
                                        <input
                                            type="file"
                                            className="file-upload-input"
                                            aria-label="File browser"
                                            name="termsAndConditionsFileURI"
                                            accept="application/pdf"
                                            onChange={handleUploadFile}
                                        />
                                        <label className="file-upload-label">
                                            {currentInput
                                                .termsAndConditionsFileURI
                                                ?.name || (
                                                <Trans i18nKey="filePlaceholder">
                                                    Choose File...
                                                </Trans>
                                            )}
                                        </label>
                                        {isFormSubmitted && (
                                            <ValidationError
                                                errorMessage={
                                                    error.termsAndConditionsFileURI
                                                }
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="tags mb-2">
                            <h4 className="uppercase text-xs">
                                <Trans i18nKey="industries">industries</Trans>
                            </h4>
                            <CreatableSelect
                                options={industries}
                                isDisabled={isIndustriesLoading}
                                isLoading={isIndustriesLoading}
                                placeholder={t("industriesPlaceHolder")}
                                onChange={handleIndustryChange}
                                defaultValue={organization.industries}
                                isMulti
                                styles={getReactSelectStyling({
                                    isMultiSelect: true,
                                })}
                            />
                        </div>
                        <div className="tags">
                            <h4 className="uppercase text-xs mb-2">
                                <Trans i18nKey="backgroundColor">
                                    Background Color
                                </Trans>
                            </h4>
                            <div className="flex">
                                <div
                                    className={`${
                                        showColorPicker && "hide"
                                    } color-square-block`}
                                    style={{
                                        backgroundColor: colorPickerColor,
                                    }}
                                    onClick={() => toggleColorPicker()}
                                ></div>
                                <div ref={colorPickerRef}>
                                    <BlockPicker
                                        triangle="hide"
                                        color={colorPickerColor}
                                        colors={COLOR_PICKER_DEFAULTS}
                                        onChange={handleColorPickerChange}
                                        className={`${
                                            !showColorPicker && "hide"
                                        }`}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="main-customer-form-bnt flex items-center justify-end float-right mt-5">
                            <button
                                onClick={onCancelClick}
                                className="secondary-blue-btn mr-2"
                            >
                                <Trans i18nKey="cancel">Cancel</Trans>
                            </button>
                            <button
                                onClick={handleSubmit}
                                className={`default-orange-outline-btn ${
                                    isSubmitting ? "disabled" : ""
                                }`}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="animate-spin mr-3 inline-flex">
                                            <i className="fas fa-sync"></i>
                                        </span>
                                        <Trans i18nKey="requesting">
                                            Requesting...
                                        </Trans>
                                    </>
                                ) : (
                                    <Trans i18nKey="editProfile">
                                        Edit Profile
                                    </Trans>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default OrganizationProfileForm;
